import React from 'react'
import 'styles/d3/sortabletable.scss'
import { getRegexFromTerm } from 'utils/search'
import { Checkbox } from 'components/UIElements'
import PropTypes from 'prop-types'

const classNames = require('classnames')

/*
SORTABLE TABLE HOC

rowList is a list of arrays, each array in the list contains objects that represent a cell in the table.
Cells must have a key which refers to the column key, a  value which is the value of the cell, and a sortableValue
which is used by the sorting functions.

To custom render a cell, pass in a renderFunction object with keys representing the  column key, with values being
react functions that return a component (these functions will be passed the individual cell objects for rendering)

rowList example format:

The below rowList will be rendered into a 3 x 3 table (excluding headerand footer)

rowList = [
  // row 1
  [
    {key: 'col_1_key', value: 'col_1_value', sortValue: 'col_1_sortableValue'}
    {key: 'col_2_key', value: 'col_2_value', sortValue: 'col_2_sortableValue'}
    {key: 'col_3_key', value: 'col_3_value', sortValue: 'col_3_sortableValue'}
  ],
  // row 2
  [
    {key: 'col_1_key', value: 'col_1_value', sortValue: 'col_1_sortableValue'}
    {key: 'col_2_key', value: 'col_2_value', sortValue: 'col_2_sortableValue'}
    {key: 'col_3_key', value: 'col_3_value', sortValue: 'col_3_sortableValue'}
  ],
]

headerOptions = [
  { key: 'keyName', sortable: boolean, component: <ReactComponent /> }
]

*/

class SortableTable extends React.Component {
  constructor(props) {
    super(props)
    this.renderFunctions = props.renderFunctions || {}
    this.state = {
      sortingBy: this.props.sortingBy,
      ascending: this.props.initialOrder ? this.props.initialOrder === 'ascending' : true,
      keyIndexMap: this._getKeyIndexMap(),
    }
  }

  onCheck = row => {
    return e => {
      const { checked, onUpdateChecked } = this.props
      e.stopPropagation()
      const newChecked = Object.assign({}, checked)
      if (newChecked.hasOwnProperty(row.id)) {
        delete newChecked[row.id]
      } else {
        newChecked[row.id] = row.payload
      }
      if (typeof onUpdateChecked === 'function') onUpdateChecked(newChecked)
    }
  }

  onCheckAll = () => {
    const { checked, onUpdateChecked } = this.props
    const checkedRows = {}
    if (Object.keys(checked).length === 0) {
      this.currentList.forEach(row => {
        checkedRows[row.id] = row.payload
      })
    }
    if (typeof onUpdateChecked === 'function') onUpdateChecked(checkedRows)
  }

  handleSort = key => {
    return () => {
      const { ascending, sortingBy } = this.state
      this.setState({
        sortingBy: key,
        ascending: key === sortingBy ? !ascending : true,
      })
    }
  }

  handleRowClick = rowData => {
    const { onRowClick } = this.props
    return () => {
      if (onRowClick) {
        onRowClick(rowData)
      }
    }
  }

  getList = () => {
    const { rowList } = this.props
    return this.getSearchedList(this.getSortedList(this.getFilteredList(rowList)))
  }

  getSortedList = list => {
    const { keyIndexMap, sortingBy } = this.state
    const idx = keyIndexMap[sortingBy] || 0
    return list.sort(this._alphanumericSort(idx))
  }

  getSearchedList = list => {
    const { searchTerm, searchKeys } = this.props
    const { keyIndexMap } = this.state

    if (!searchTerm || !searchKeys) return list
    const regex = getRegexFromTerm(searchTerm)
    return list.filter(row => {
      for (let i = 0; i < searchKeys.length; i++) {
        const key = searchKeys[i]
        const value = `${row[keyIndexMap[key]].value}`
        if (value.match(regex)) {
          return true
        }
      }
      return false
    })
  }

  getFilteredList = list => {
    const { filters } = this.props
    const { keyIndexMap } = this.state

    const filterKeys = Object.keys(filters || {})
    if (filterKeys.length > 0) {
      return list.filter(row => {
        for (const key in filters) {
          const colIdx = keyIndexMap[key]
          const filter = filters[key]
          if (!filter(row[colIdx])) return false
        }
        return true
      })
    }
    return list
  }

  _alphanumericSort = idx => {
    const { ascending } = this.state

    return (prev, next) => {
      const a = prev[idx].sortValue
      const b = next[idx].sortValue
      if (typeof a === 'string') {
        return ascending
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : b.toLowerCase().localeCompare(a.toLowerCase())
      }
      return ascending ? a - b : b - a
    }
  }

  _getKeyIndexMap() {
    const { headerOptions } = this.props
    const map = {}
    headerOptions.forEach((option, idx) => {
      map[option.key] = idx
    })
    return map
  }

  renderSortedRows = () => {
    const { checked, emptyText, onRowClick, optionalCellProps, rowClassName, rowSelectOptions, rowList } = this.props
    const { isRowSelected, selectedRowClassName } = rowSelectOptions || {}
    const currentList = this.getList()

    if (emptyText && currentList.length === 0)
      return (
        <tr>
          <td>{emptyText}</td>
        </tr>
      )

    return this.currentList.map((row, idx) => {
      const isSelected = isRowSelected && isRowSelected(row)
      const trClassName = classNames({
        clickable: onRowClick,
        [rowClassName]: rowClassName,
        [selectedRowClassName]: isSelected,
      })
      return (
        <tr className={trClassName} onClick={this.handleRowClick(row)} key={`row_${idx}`}>
          {checked && (
            <td onClick={this.onCheck(row, idx)} className='checkbox-cell'>
              <Checkbox checked={checked.hasOwnProperty(row.id)} />
            </td>
          )}
          {row.map((cellProps, rowIdx) => {
            return this.renderFunctions[cellProps.key] ? (
              this.renderFunctions[cellProps.key](
                Object.assign({}, cellProps, optionalCellProps, { isSelected }),
                rowIdx,
              )
            ) : (
              <td key={`col_${rowIdx}`} className={cellProps.className}>
                {cellProps.value}
              </td>
            )
          })}
        </tr>
      )
    })
  }

  renderHeaderRow = () => {
    const { checked, headerOptions, offset } = this.props
    const { ascending, sortingBy } = this.state
    let header = []
    if (checked) {
      header.push(
        <th key='checkbox-header' onClick={this.onCheckAll} className='checkbox-cell' style={{ top: `${offset}px` }}>
          <Checkbox checked={Object.keys(checked).length > 0} />
        </th>,
      )
    }
    header = header.concat(
      headerOptions.map((option, idx) => {
        let className = option.sortable ? 'clickable' : ''
        className += option.key === sortingBy ? ' active' : ''
        className += ascending ? ' asc' : ' desc'
        return (
          <th
            className={className}
            onClick={option.sortable ? this.handleSort(option.key) : null}
            key={`header_${idx + 1}`}
            style={{ top: `${offset}px` }}
          >
            <span>{option.component}</span>
            {option.sortable && (
              <span className='sort-arrows'>
                <i className='fas fa-caret-up' />
                <i className='fas fa-caret-down' />
              </span>
            )}
          </th>
        )
      }),
    )
    return header
  }

  renderFooter = () => {
    const { footerOptions, renderFooter } = this.props
    if (renderFooter) return renderFooter(this.currentList)
    if (!footerOptions) return null
    return (
      <tr>
        {footerOptions.map((option, idx) => {
          return <td key={`footer_${idx}`}>{option.component}</td>
        })}
      </tr>
    )
  }

  render() {
    const { className, renderHeader } = this.props
    this.currentList = this.getList()
    return (
      <div className={`d3-sortable-table ${className || ''}`}>
        {renderHeader && renderHeader(this.currentList)}
        <table>
          <thead>
            <tr className='sticky'>{this.renderHeaderRow()}</tr>
          </thead>
          <tbody>{this.renderSortedRows()}</tbody>
          <tfoot>{this.renderFooter()}</tfoot>
        </table>
      </div>
    )
  }
}

SortableTable.propTypes = {
  checked: PropTypes.object, // adds a checklist column (required for checklist)
  className: PropTypes.string, // classname for component
  emptyText: PropTypes.string, // text to display if table is empty
  filters: PropTypes.object, // filter functions for the table
  footerOptions: PropTypes.array, // options for footer
  headerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      sortable: PropTypes.bool,
      component: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    }),
  ).isRequired, // options/key values for each column
  initialOrder: PropTypes.oneOf(['ascending', 'descending']), // initial sort ascending or descending
  offset: PropTypes.number,
  onRowClick: PropTypes.func, // callback for row click - row props passed as arguments
  onUpdateChecked: PropTypes.func, // callback for when a row is "checked"
  optionalCellProps: PropTypes.object, // props to be passed to every cell - includes result of isRowSelected if provided
  renderFooter: PropTypes.func, // render function for table footer
  renderFunctions: PropTypes.objectOf(PropTypes.func), // render function specified column types
  renderHeader: PropTypes.func, // render function for component above table
  rowClassName: PropTypes.string, // className for each row in table
  rowList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.number, PropTypes.string]),
        sortValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
      }),
    ),
  ).isRequired, // list of row options,
  rowSelectOptions: PropTypes.shape({
    isRowSelected: PropTypes.func, // func that takes row object and returns a bool
    selectedRowClassName: PropTypes.string, // className to be added to tr in sorted rows if isRowSelected returns true
  }),
  searchKeys: PropTypes.arrayOf(PropTypes.string), // keys to the column values that will be searched
  searchTerm: PropTypes.string, // search term to filter by
  sortingBy: PropTypes.string, // initial sort key
}

export default SortableTable



// WEBPACK FOOTER //
// ./src/components/SortableTable.js